<template>
  <main>
    <section class="hero">
      <div class="hero-content">
        <h2>Courses</h2>
        <p>Free, self-paced curriculum developed by Space Coast STEM</p>
      </div>
    </section>

    <section class="content-section">
      <div class="container">
        <router-link to="/students" class="back-link">&larr; Back to Student Resources</router-link>

        <h2>Explore Our Courses</h2>
        <p>Each course is free and self-paced, with materials you can use at home or in the classroom.</p>

        <div class="course-grid">
          <a
            v-for="course in courses"
            :key="course.title"
            :href="course.url"
            class="course-card"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div class="course-icon">{{ course.icon }}</div>
            <h3>{{ course.title }}</h3>
            <p>{{ course.description }}</p>
            <span class="course-link">View Course &rarr;</span>
          </a>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
const courses = [
  {
    title: 'Intro to Computers',
    icon: '🖥️',
    description: 'Learn the fundamentals of how computers work, from hardware to operating systems. A great starting point for new students.',
    url: 'https://spacecoaststem.gitlab.io/courses/intro-to-computers/'
  },
  {
    title: 'Intro to Coding',
    icon: '💻',
    description: 'A beginner-friendly introduction to programming concepts, designed for students with no prior experience.',
    url: 'https://spacecoaststem.gitlab.io/courses/intro-to-coding'
  }
]
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  padding: 8rem 2rem;
  text-align: center;
}

.hero-content h2 {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
}

.hero-content p {
  font-size: 1.3rem;
  opacity: 0.95;
}

.content-section {
  padding: 5rem 2rem;
}

.back-link {
  display: inline-block;
  color: var(--secondary-color, #3ba3d1);
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 2rem;
}

.back-link:hover {
  text-decoration: underline;
}

.content-section h2 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.content-section p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-light, #666666);
  margin-bottom: 1rem;
}

.course-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.course-card {
  background: var(--white, #ffffff);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
}

.course-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.course-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.course-card h3 {
  color: var(--primary-color, #2c4a6b);
  margin-bottom: 1rem;
  margin-top: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 1.4rem;
}

.course-card p {
  color: var(--text-light, #666666);
  line-height: 1.6;
  flex-grow: 1;
  margin-bottom: 1.25rem;
}

.course-link {
  color: var(--secondary-color, #3ba3d1);
  font-weight: 600;
}

@media (max-width: 768px) {
  .hero-content h2 {
    font-size: 2rem;
  }

  .hero {
    padding: 4rem 2rem;
  }

  .content-section h2 {
    font-size: 2rem;
  }

  .course-grid {
    grid-template-columns: 1fr;
  }
}
</style>
